import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import dropDownIcon from "../../../../img/icons/dropdown-icon.svg";
import localization from "../../../../data/config";
import Reset from "../Reset";
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from "prop-types";
import {
  checkboxToHash,
  findHashValue,
  hashToCheckbox,
  updateHash,
} from "../../../../utilities/hash";
import { PARAMETER_VALUE } from "../../../../constants/config/hash";
import { match } from "../../../../utilities/arrays";
import { track } from "../../../../actions/dtm";
import { FILTER, LAST_FILTERED_PARAM } from "../../../../constants/config/dtm";
import { useDispatch, useSelector } from "react-redux";
import { resetColumn } from "../../../../actions/filters";
import AndOr from "./AndOr";
import "./style.css";

/**
 * @name Checkboxes
 * @function
 * @description Displays Checkbox filter
 * @returns {JSX}
 * @param {*} props
 */
const Checkboxes = (props) => {
  const [isOpen, setIsOpen] = useState(false),
    [selectedOption, setSelectedOption] = useState(""),
    [focusedIndex, setFocusedIndex] = useState(-1),
    [totalOptions, setTotalOptions] = useState(props.options.length),
    [totalLabel, setTotalLabel] = useState(props.options.length),
    [selectedCheckboxes, setSelectedCheckboxes] = useState([]),
    [selectAllChecked, setSelectAllChecked] = useState(false),
    [resetSignal, setResetSignal] = useState(false),
    optionsList = useMemo( () => {
      return props.options
        ? props.options.map( option => formatValue(option.value) )
        : [];
    }, [ props.options ]),
    [allOptions, setAllOptions] = useState([...optionsList]),
    [and, setAnd] = useState(false),
    [ includeBlanks, setIncludeBlanks ] = useState( true ),
    [ disableBlanks, setDisabledBlanks ] = useState( false ),
    dropdownContainerRef = useRef(null),
    dispatch = useDispatch();
  const { labelSelectAll, lastReset, newSort, sortPriority } = useSelector(
    (state) => ({
      labelSelectAll: state.labels.button.selectAll,
      lastReset: state.filters.lastReset,
      newSort: state.filters.newSort,
      sortPriority: state.filters.sortPriority,
    })
  );

  /**
   * Determines if all options should be selected
   * @param {string} value unused
   * @param {boolean} all Determines if all options should be selected
   */
  const selectAll = useCallback((value, all = true) => {
    setIncludeBlanks(all);
    setSelectedCheckboxes(all ? [...allOptions] : []);
  }, [allOptions]);
  
  /**
   * Retrieves the current total of selected options
   * @returns {number}
   */
   const selectedCount = () => ( selectedCheckboxes.length + ( includeBlanks ? 1 : 0 ) );

  /**
   * to open and close Dropdown Filters
   */
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  /**
   * Handle Check functionality for all dropdowns
   */
  const handleOptionSelect = (event, option) => {
    event.stopPropagation();
    setSelectedOption(option);
    toggleCheckbox(option.value);
  };

  /**
   * Normalizes a value by converting it to a number if it's numeric.
   * 
   * If the value is not a number (NaN), it returns the original value.
   * Otherwise, it converts and returns it as a Number.
   *  
   * @param { string | number} val - The value to normalize. 
   * @returns { string | number } - The normalized value, as a number if applicable.
   */
  const normalizeValue = (val) => {
    return isNaN(val) ? val : Number(val);
  };

  /**
   * Handles checkbox Toggle
   */
  const toggleCheckbox = (value) => {

    const normalizedVal = normalizeValue(value);
        
    const currentIndex = selectedCheckboxes.indexOf(normalizedVal);    
    const newSelectedCheckboxes = [...selectedCheckboxes];

    currentIndex === -1
    ? newSelectedCheckboxes.push(normalizedVal)
    : newSelectedCheckboxes.splice(currentIndex, 1); 

    setSelectedCheckboxes(newSelectedCheckboxes);
  
    const adobeDataName = props.name;

    const dataForAdobeDataLayer = {
      event: "PST Table Interaction",
      pstInteractionsInfo: {
        [adobeDataName]: newSelectedCheckboxes.join('|')
      }
    }
    window.adobeDataLayer = window.adobeDataLayer || [];
    window.adobeDataLayer.push(dataForAdobeDataLayer);
  
  };

  /**
   * toggles inclusion of empty fields
   */
    const toggleBlanks = () => setIncludeBlanks( !includeBlanks );

  /**
   * Handles resetFilter
   */
  const resetFilter = () => {
    selectAll(null, true);
    setResetSignal(true);
    if (newSort && newSort === props.parameter) {
      dispatch(resetColumn(true, props.parameter));
    }
    // When the reset is clicked, focus should go back do dropdown
    const dropdownFocus = dropdownContainerRef.current.querySelector('.custom-dropdown__container__dropdown-header');
    dropdownFocus.focus();
  };

  /**
   * Handles the header title
   */
  const headerTitle =  selectedCheckboxes.length != totalOptions
    ? `${localization.parameterValues}`
    : `${localization.defaultValues}`

  /**
   * Handles the keys arrows Up and Down, Enter and Escape for accessibility
   */
  const handleKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        e.preventDefault();
        const direction = e.key === 'ArrowDown' ? 1 : -1;
        const options = document.querySelectorAll('.dropdown-options__option');
        let newIndex = focusedIndex + direction;
  
        if (newIndex < 0) {
          newIndex = options.length - 1;
        } else if (newIndex >= options.length) {
          newIndex = 0;
        }
  
        setFocusedIndex(newIndex);
        options[newIndex].focus();
        break;
  
        case 'Enter':
          if (isOpen) {
            const focusedOption = document.querySelector('.dropdown-options__option:focus');
            if (focusedOption) {
              const optionValue = focusedOption.querySelector('input').value;
              toggleCheckbox(optionValue);
            }
          } else {
            setIsOpen(true);
          }
          break;
  
      case 'Escape':
        setIsOpen(false);
        break;
  
      default:
        break;
    }
  };

  // set initial values; must occur first! VERY important
  useEffect(() => {
    let hash = findHashValue(PARAMETER_VALUE.replace("{id}", props.parameter)),
      values = [],
      hasBlanks = false;

    if (!hash) {
      selectAll(null, true);
    } else {
      hashToCheckbox(hash)[props.parameter].forEach((comparison) => {
        let tempVal = comparison.value;
        if (tempVal.length > 0) {
          values.push(formatValue(tempVal));
        } else {
          hasBlanks = true;
        }
      });

      if( hasBlanks !== includeBlanks ) setIncludeBlanks( hasBlanks );
      setSelectedCheckboxes(values);
    }
  }, [props.parameter, lastReset, selectAll]);

  // set options list
  useEffect(() => {
    setTotalLabel( optionsList.length + 1 );
    if (!match(optionsList, allOptions)) setAllOptions([...optionsList]);
  }, [setAllOptions, allOptions, includeBlanks, optionsList]);

  // update hash when selections change
  useEffect(() => {
    const hash =
      allOptions.length !== selectedCheckboxes.length || !includeBlanks || and
        ? checkboxToHash(props.parameter, selectedCheckboxes, (includeBlanks && !disableBlanks)) : null;


    updateHash(props.parameter, hash);

    if (hash) {
      window[LAST_FILTERED_PARAM] = hash;
      dispatch(track(FILTER));
    }
  }, [
    allOptions,
    selectedCheckboxes,
    includeBlanks,
    props.parameter,
    disableBlanks,
    and,
    updateHash,
    checkboxToHash,
    resetSignal,
  ]);

 // Determine if blanks need to be disabled
  useEffect(() => {
    setDisabledBlanks( props.parts ? !props.parts.some( value => !value[ props.parameter ]?.displayValue || value[ props.parameter ].displayValue === '' ) : props.includeBlanks );
}, [ props.parts, setDisabledBlanks ]);  

  // Update "Select All" state based on individual checkboxes
  useEffect(() => {
    if (selectedCheckboxes.length === totalOptions) {
      setSelectAllChecked(true); // All checkboxes are selected
    } else {
      setSelectAllChecked(false); // At least one checkbox is unselected
    }
  }, [selectedCheckboxes, totalOptions]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownContainerRef.current && !dropdownContainerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  /**
   * @name formatValue
   * @function
   * @description
   * @param {string|number} value
   * @returns {string|number}
   */
  function formatValue(value) {
    var val;
    try {
      val = decodeURI(value);
    } catch {
      val = value;
    }
    return isNaN(value) ? val : Number(value);
  }

  /**
   * Checks if a given value exists in the selectedCheckboxes array
   * with type normalization (e.g., converts numeric strings to numbers).   * 
   * @param  { string | number } value - The value to check from the option
   * @returns { boolean } - Returns true if the normalized value exists in selectedCheckboxes.
   */
  const isChecked = (value) => {
    const normalizedValue = normalizeValue(value);
    return selectedCheckboxes.includes(normalizedValue);
  };

  return (
    <div className="custom-dropdown" ref={dropdownContainerRef}>
      <div className="custom-dropdown__container">
        <div
          className="custom-dropdown__container__dropdown-header"
          tabIndex={0} 
          role="combobox"
          aria-label={`${props.name}`} aria-expanded={isOpen}
          onClick={toggleDropdown}
          onKeyDown={handleKeyDown}
        >
          <span className="dropdown-header__title">
            {headerTitle}
          </span>
          <span className={`dropdown-header__icon ${isOpen ? "open" : ""}`}>
            <img src={dropDownIcon} alt="Dropdown Icon" aria-hidden="true"/>
          </span>
          {isOpen && (
            <ul className="dropdown-options">
              <li
              tabIndex={focusedIndex === -1 ? 0 : -1}
                className="dropdown-options__option"
                onClick={(event) => {
                  event.stopPropagation();
                  selectAll(null, !selectAllChecked);
                }}
                role="option"
                aria-checked={selectAllChecked}
              >
                <input
                  type="checkbox"
                  checked={selectAllChecked}
                  onChange={() => selectAll(null, !selectAllChecked)}
                  id={`select-all-${props.parameter}`}
                  className="select-all-checkbox checkbox-global__input"
                  aria-hidden="true"
                />
                {labelSelectAll}
              </li>
              <li
                className={`dropdown-options__option ${disableBlanks ? 'disabled' : ''}`}
                onClick={(event) => {
                  event.stopPropagation();
                  if (!disableBlanks) {
                    toggleBlanks();
                  }
                }}
              >
                <input
                  className={`include-blanks-checkbox checkbox-global__input ${disableBlanks ? 'disabled' : ''}`}
                  type="checkbox"
                  checked={includeBlanks}
                  onChange={() => {
                    if (!disableBlanks) {
                      toggleBlanks();
                    }
                  }}
                  id={`include-blanks-${props.parameter}`} 
                />
              {props.labelBlanks}
              </li>
              {props.options.map((option, index) => (
                <li
                  key={option.value}
                  className="dropdown-options__option"
                  onClick={(event) => handleOptionSelect(event, option)}
                  tabIndex={focusedIndex === index ? 1 : -1}
                  role="option"
                  aria-checked={selectAllChecked || isChecked(option.value)}
                >
                  <input
                    type="checkbox"
                    value={option.value}
                    checked={
                      selectAllChecked ||
                      isChecked(option.value)
                    }
                    onChange={() => toggleCheckbox(option.value)}
                    id={`filter-${props.parameter}-${option.value}`}
                    key={`option-${index}`}
                    aria-hidden="true"
                  />
                  <span dangerouslySetInnerHTML={{ __html: option.value }} />
                </li>
              ))}
            </ul>
          )}
        </div>
        <span className="custom-dropdown__container__selected-filters">
        {selectedCount()}/{totalLabel}{" "}
        {props.label.valuesSelected}
</span>
      </div>
      {selectedCheckboxes.length >= 1 &&
        selectedCheckboxes.length <= totalOptions && (
          <AndOr
          value={localization.matchAllValues}
          parameter={props.parameter}
          callback={setAnd}
          resetSignal={resetSignal}
          setResetSignal={setResetSignal}
          ariaLabel = {props.tooltip}
        />
        )}
      {(selectedCheckboxes.length != totalOptions || !includeBlanks ||
        (newSort === props.parameter && sortPriority.length > 0) ||
        and) && <Reset onClick={resetFilter} />}
    </div>
  );
};

Checkboxes.defaultProps = {
  includeBlanks: true
};

Checkboxes.propTypes = {
  parameter: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  lastReset: PropTypes.number,
  label: PropTypes.shape({
    valuesSelected: PropTypes.string,
  }),
};

const mapStateToProps = state => ({
  labelBlanks: state.labels.button.blanks,
  parts: state.parts.allData,
});

const mapDispatchToProps = dispatch => bindActionCreators({ track }, dispatch );

export default connect( mapStateToProps, mapDispatchToProps ) (Checkboxes);